// iter487 — Mind & Body Lab Phase 2 + 3 components.
// Exports: WeeklyPlanCard, SynergyScoreCard, AiCoachChat, CorrelationsCard
// Used inside MindBodyLabPage.

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Sparkles, Loader2, Send, Bot, User as UserIcon, TrendingDown, TrendingUp,
  Activity, Salad, Zap, Brain, AlertCircle, Wand2,
} from "lucide-react";

const CAT_COLORS = {
  exercise:  "from-emerald-500 to-emerald-600",
  mind_body: "from-violet-500 to-violet-600",
  sleep:     "from-indigo-500 to-indigo-700",
  recovery:  "from-amber-500 to-amber-600",
  stress:    "from-pink-500 to-pink-600",
};

// ────────────────────────────────────────────────────────────────────────────
// 1. Weekly Plan Card (rule-based + AI-augmented)
// ────────────────────────────────────────────────────────────────────────────
export function WeeklyPlanCard({ API, profile, lang, isDark }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useAi, setUseAi] = useState(false);
  const bg = lang === "bg";

  const fetchPlan = async (withAi = false) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mindbody/weekly-plan`, {
        conditions: profile.conds || [],
        symptoms:   profile.symps || [],
        goals:      profile.goals || [],
        use_ai:     withAi,
        language:   lang,
      });
      setPlan(r.data);
      if (withAi && r.data.ai_model) {
        toast.success(bg ? "GPT-5.2 рационализира плана" : "GPT-5.2 enriched the plan");
      }
    } catch {
      toast.error(bg ? "Грешка при генериране" : "Failed to generate");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch initial plan
  useEffect(() => { fetchPlan(false); /* eslint-disable-next-line */ }, [profile.conds, profile.symps, profile.goals]);

  const txt   = isDark ? "text-slate-100" : "text-slate-900";
  const sub   = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <section data-testid="mindbody-weekly-plan" className={`rounded-2xl border p-5 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-pink-500 shrink-0">
          <CalendarDays className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-base font-bold ${txt}`}>
            {bg ? "Седмичен план" : "Weekly Plan"}
          </h2>
          <p className={`text-[11px] mt-0.5 ${muted}`}>
            {bg ? "Лични интервенции, разпределени за 7 дни" : "Personal interventions across 7 days"}
          </p>
        </div>
        <button
          onClick={() => { setUseAi(true); fetchPlan(true); }}
          disabled={loading}
          data-testid="mindbody-weekly-ai-btn"
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${
            isDark ? "bg-gradient-to-r from-fuchsia-500/30 to-pink-500/30 text-fuchsia-200 border border-fuchsia-500/40 hover:from-fuchsia-500/40 hover:to-pink-500/40"
                   : "bg-gradient-to-r from-fuchsia-50 to-pink-50 text-fuchsia-700 border border-fuchsia-200 hover:from-fuchsia-100 hover:to-pink-100"
          } disabled:opacity-50`}
        >
          {loading && useAi ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
          {bg ? "Подобри с AI" : "Enhance with AI"}
        </button>
      </div>

      {plan && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="mindbody-weekly-grid">
            {plan.plan.map((day) => (
              <div key={day.day_index} className={`rounded-xl border p-2.5 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${muted} mb-1.5`}>
                  {day.day_label.slice(0, 3)}
                </div>
                {day.items.length === 0 ? (
                  <div className={`text-[10px] ${muted}`}>{bg ? "Почивка" : "Rest"}</div>
                ) : day.items.map((it, i) => (
                  <div key={i} className="mb-1.5 last:mb-0">
                    <div className={`flex items-center gap-1 mb-0.5`}>
                      <span className={`w-4 h-4 rounded shrink-0 bg-gradient-to-br ${CAT_COLORS[it.category]}`}></span>
                      <span className={`text-[10px] font-semibold truncate ${txt}`}>{it.name}</span>
                    </div>
                    <div className={`text-[9.5px] ${muted} line-clamp-2 pl-5`}>{it.dose}</div>
                  </div>
                ))}
                {day.total_min > 0 && (
                  <div className={`text-[9px] mt-1 pt-1 border-t ${isDark ? "border-slate-700 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                    ~{day.total_min} {bg ? "мин" : "min"}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Rationale + tips */}
          <div className={`mt-3 p-3 rounded-xl text-[12px] ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
            <div className={`${txt} font-semibold mb-1 flex items-center gap-1.5`}>
              {plan.ai_model && <Sparkles className="w-3 h-3 text-fuchsia-400" />}
              {bg ? "Защо този план" : "Why this plan"}
            </div>
            <p className={sub}>{bg ? plan.rationale_bg : plan.rationale_en}</p>
            {(plan.tips_bg || plan.tips_en) && (
              <ul className="mt-2 space-y-0.5">
                {(bg ? plan.tips_bg : plan.tips_en).map((tip, i) => (
                  <li key={i} className={`flex gap-1.5 text-[11px] ${sub}`}>
                    <span className="text-fuchsia-500 shrink-0">✓</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {loading && !plan && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Synergy Score Card (Food + Lifestyle combo)
// ────────────────────────────────────────────────────────────────────────────
export function SynergyScoreCard({ API, profile, lang, isDark, onOpenFoodlab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const bg = lang === "bg";

  useEffect(() => {
    setLoading(true);
    axios.post(`${API}/mindbody/synergy/score`, {
      conditions: profile.conds || [],
      symptoms:   profile.symps || [],
      goals:      profile.goals || [],
    }).then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API, profile.conds, profile.symps, profile.goals]);

  const txt   = isDark ? "text-slate-100" : "text-slate-900";
  const sub   = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  if (loading && !data) {
    return (
      <section className={`rounded-2xl border p-5 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
        <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
      </section>
    );
  }
  if (!data) return null;
  const score = data.synergy || 0;
  const scoreColor = score >= 70 ? "from-emerald-500 to-emerald-600"
                  : score >= 50 ? "from-cyan-500 to-cyan-600"
                  : score >= 30 ? "from-amber-500 to-amber-600"
                  : "from-slate-400 to-slate-500";

  return (
    <section data-testid="mindbody-synergy-score" className={`rounded-2xl border p-5 bg-gradient-to-br ${isDark ? "from-violet-900/30 via-fuchsia-900/20 to-pink-900/20 border-fuchsia-700/40" : "from-violet-50 via-fuchsia-50 to-pink-50 border-fuchsia-200"}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center justify-center shrink-0">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center bg-gradient-to-br ${scoreColor} shadow-lg`}>
            <div className="text-center text-white">
              <div className="text-[9px] font-bold uppercase tracking-wider leading-none">SYNERGY</div>
              <div className="text-2xl font-black leading-tight">{score.toFixed(0)}</div>
              <div className="text-[8px] opacity-80 leading-none">/100</div>
            </div>
          </div>
          <div className={`mt-2 flex items-center gap-1.5 text-[10px] ${muted}`}>
            <span className="flex items-center gap-1"><Salad className="w-2.5 h-2.5" />{data.fms_mean?.toFixed(0)}</span>
            <span>+</span>
            <span className="flex items-center gap-1"><Activity className="w-2.5 h-2.5" />{data.ims_mean?.toFixed(0)}</span>
            {data.bonus > 0 && <span className="ml-1">+{data.bonus}</span>}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-base font-bold ${txt} mb-1`}>
            {bg ? "Synergy Score (храна + lifestyle)" : "Synergy Score (food + lifestyle)"}
          </h2>
          <p className={`text-[12px] ${sub} mb-2`}>{bg ? data.explanation_bg : data.explanation_en}</p>
          {data.overlap_phenotypes?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {data.overlap_phenotypes.map((p) => (
                <span key={p} className={`text-[9.5px] px-1.5 py-0.5 rounded ${isDark ? "bg-fuchsia-500/20 text-fuchsia-200" : "bg-fuchsia-100 text-fuchsia-700"}`}>
                  ⚡ {p}
                </span>
              ))}
            </div>
          )}
          {(!data.top_foods || data.top_foods.length === 0) && (
            <button
              onClick={onOpenFoodlab}
              data-testid="mindbody-synergy-open-foodlab"
              className={`text-[11px] font-bold px-3 py-1.5 rounded-lg ${isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
            >
              {bg ? "Добави храни в Nutrition Lab →" : "Add foods in Nutrition Lab →"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 3. AI Lifestyle Coach Chat (GPT-5.2 multi-turn)
// ────────────────────────────────────────────────────────────────────────────
export function AiCoachChat({ API, profile, lang, isDark }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const bg = lang === "bg";

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const placeholders = bg
    ? "Питай за движение, сън, стрес... напр. „Какво да направя вечер за по-добър сън?“"
    : "Ask about movement, sleep, stress... e.g. \"What to do in the evening for better sleep?\"";

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    try {
      const r = await axios.post(`${API}/mindbody/coach/chat`, {
        message:    text,
        session_id: sessionRef.current,
        conditions: profile.conds || [],
        symptoms:   profile.symps || [],
        goals:      profile.goals || [],
        language:   lang,
      }, { timeout: 60000 });
      sessionRef.current = r.data.session_id;
      const piiNote = r.data.pii_scrubbed && Object.keys(r.data.pii_scrubbed).length
        ? (bg ? "✓ Лични данни маскирани преди изпращане" : "✓ Personal identifiers masked before send")
        : null;
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, pii: piiNote }]);
    } catch (e) {
      const msg = e.response?.data?.detail || (bg ? "Грешка при AI отговор" : "AI error");
      setMessages((m) => [...m, { role: "assistant", text: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const txt   = isDark ? "text-slate-100" : "text-slate-900";
  const sub   = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <section data-testid="mindbody-coach" className={`rounded-2xl border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} flex flex-col`} style={{ height: "440px" }}>
      <div className={`flex items-center gap-2 p-4 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-500">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-sm font-bold ${txt}`}>
            {bg ? "AI Lifestyle Coach" : "AI Lifestyle Coach"}
          </h2>
          <p className={`text-[10px] ${muted}`}>
            {bg ? "GPT-5.2 · educational coaching · PII scrubbed" : "GPT-5.2 · educational coaching · PII scrubbed"}
          </p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="mindbody-coach-history">
        {messages.length === 0 ? (
          <div className={`text-center py-8 ${muted}`}>
            <Bot className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-xs">{bg ? "Започни разговор — попитай как да си помогнеш с lifestyle интервенции." : "Start a chat — ask how to help yourself with lifestyle interventions."}</p>
          </div>
        ) : messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-500 shrink-0">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? (isDark ? "bg-violet-600/30 text-violet-100" : "bg-violet-100 text-violet-900")
                : m.error
                  ? (isDark ? "bg-rose-900/40 text-rose-200" : "bg-rose-50 text-rose-700")
                  : (isDark ? "bg-slate-800 text-slate-100" : "bg-slate-100 text-slate-900")
            }`}>
              {m.text}
              {m.pii && <div className={`text-[9.5px] mt-1.5 opacity-70 italic ${sub}`}>{m.pii}</div>}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-violet-600/40 shrink-0">
                <UserIcon className="w-3.5 h-3.5 text-violet-200" />
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-500 shrink-0">
              <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
            </div>
            <div className={`text-[12px] ${muted}`}>{bg ? "Coach пише..." : "Coach is typing..."}</div>
          </div>
        )}
      </div>

      <div className={`p-3 border-t ${isDark ? "border-slate-800" : "border-slate-200"} flex gap-2`}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={placeholders}
          disabled={sending}
          maxLength={1000}
          data-testid="mindbody-coach-input"
          className={`flex-1 px-3 py-2 rounded-lg text-[12.5px] border outline-none focus:ring-2 focus:ring-fuchsia-500/40 ${
            isDark ? "bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                   : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
          }`}
        />
        <button
          onClick={send}
          disabled={!input.trim() || sending}
          data-testid="mindbody-coach-send"
          className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 4. Symptom Correlations Card
// ────────────────────────────────────────────────────────────────────────────
export function CorrelationsCard({ API, lang, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bg = lang === "bg";

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/mindbody/correlations`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const txt   = isDark ? "text-slate-100" : "text-slate-900";
  const sub   = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <section data-testid="mindbody-correlations" className={`rounded-2xl border p-5 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-500 to-emerald-500 shrink-0">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-sm font-bold ${txt}`}>
            {bg ? "Symptom Correlation Analytics" : "Symptom Correlation Analytics"}
          </h2>
          <p className={`text-[10px] mt-0.5 ${muted}`}>
            {bg ? "Връзка между записани интервенции и симптоми" : "Link between tracked interventions and symptoms"}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
      ) : data?.insufficient_data ? (
        <div className={`text-[12px] p-3 rounded-lg space-y-2 ${isDark ? "bg-amber-500/10 text-amber-200 border border-amber-500/30" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{bg ? data.explanation_bg : data.explanation_en}</span>
          </div>
          {/* iter509 — Transparent debug: tell user EXACTLY what they have vs need */}
          {(data.snapshots_count !== undefined || data.symptom_log_days !== undefined) && (
            <div className={`grid grid-cols-2 gap-2 pt-2 border-t ${isDark ? "border-amber-500/20" : "border-amber-200"}`}>
              <div data-testid="correlations-debug-snapshots">
                <div className="text-[9px] uppercase tracking-wider opacity-70">
                  {bg ? "Интервенции" : "Interventions"}
                </div>
                <div className="text-[14px] font-bold">
                  {data.snapshots_count} / {data.min_required?.snapshots ?? 1}
                </div>
              </div>
              <div data-testid="correlations-debug-symptoms">
                <div className="text-[9px] uppercase tracking-wider opacity-70">
                  {bg ? "Дни със симптоми" : "Symptom days"}
                </div>
                <div className="text-[14px] font-bold">
                  {(data.symptom_log_days ?? 0) + (data.mb_symptom_entries ?? 0)} / {data.min_required?.symptom_days ?? 1}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : data?.correlations?.length > 0 ? (
        <>
          <div className="space-y-2">
            {data.correlations.slice(0, 6).map((c, i) => {
              const improved = c.direction === "improved";
              return (
                <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${improved ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                    {improved ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12px] font-semibold ${txt}`}>
                      {c.intervention_name} {bg ? "→" : "→"} {c.symptom_id}
                    </div>
                    <div className={`text-[10px] flex items-center gap-1.5 ${muted}`}>
                      <span>n={c.n_with} / {c.n_without} {bg ? "дни" : "days"}</span>
                      {c.confidence && (
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                            c.confidence === "high"
                              ? (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                              : c.confidence === "medium"
                                ? (isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700")
                                : (isDark ? "bg-slate-500/20 text-slate-300" : "bg-slate-100 text-slate-600")
                          }`}
                        >
                          {bg ? ({high:"висока", medium:"средна", low:"ниска"})[c.confidence] : c.confidence}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`text-sm font-bold shrink-0 ${improved ? "text-emerald-500" : "text-rose-500"}`}>
                    {c.delta_pct > 0 ? "+" : ""}{c.delta_pct}%
                  </div>
                </div>
              );
            })}
          </div>
          <p className={`text-[10px] mt-2 italic ${muted}`}>{bg ? data.explanation_bg : data.explanation_en}</p>
        </>
      ) : null}
    </section>
  );
}
